
import { GENERIC_ERROR_DESCRIPTION, SOCIAL_PLATFORMS_METADATA } from '@/constants';
import { AppRoute } from '@/router/routes';
import { SocialFeedItem } from '@/types/socialFeed';
import { setCrossPostDraft } from '@/utils/crossPostDraft';
import {
  formatSocialFeedGlobalPostMeta,
  formatSocialFeedPostMeta,
  formatSocialFeedPublishedDate,
  socialFeedPlatformLabel,
} from '@/utils/socialFeed';
import {
  buildCrossPostDraft,
  deleteSocialFeedPost,
  describePostPublishError,
  getCrossPostPlatformOptions,
  getDeletePostErrorNotification,
} from '@/utils/socialFeedPostActions';
import { Bot, DEFAULT_TIMEZONE, SupportedPlatform } from 'ignite360-core';
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';

@Component({ name: 'social-feed-post-detail-modal' })
export default class SocialFeedPostDetailModal extends Vue {
  readonly instagramPlatform = SupportedPlatform.Instagram;
  readonly linkedInPlatform = SupportedPlatform.LinkedIn;
  readonly googleBusinessPlatform = SupportedPlatform.GoogleBusiness;
  readonly CAPTION_PREVIEW_LENGTH = 300;

  @Prop({ required: true, type: Boolean })
  readonly show!: boolean;

  @Prop({ required: false, type: Object, default: null })
  readonly item!: SocialFeedItem | null;

  @Prop({ required: true, type: Object })
  readonly bot!: Bot;

  @Prop({ required: false, type: String, default: () => DEFAULT_TIMEZONE })
  readonly timezone!: string;

  selectedCrossPostPlatforms: SupportedPlatform[] = [];
  isDeleting = false;
  isCrossPosting = false;
  thumbnailFailed = false;
  captionExpanded = false;

  get platformLabel(): string {
    if (!this.item) {
      return '';
    }
    const meta = SOCIAL_PLATFORMS_METADATA.find((m) => m.value === this.item!.platform);
    return meta?.label || socialFeedPlatformLabel(this.item.platform);
  }

  get platformImage(): string {
    if (!this.item) {
      return '';
    }
    const meta = SOCIAL_PLATFORMS_METADATA.find((m) => m.value === this.item!.platform);
    return meta?.image || '';
  }

  get postMetaLine(): string | null {
    if (!this.item) {
      return null;
    }
    const timezone = this.item.botTimezone || this.timezone;
    if (this.item.botName) {
      return formatSocialFeedGlobalPostMeta(
        this.item.publishedAt,
        this.item.platform,
        this.item.botName,
        timezone,
      );
    }
    return (
      formatSocialFeedPostMeta(this.item.publishedAt, this.item.platform, timezone) ||
      formatSocialFeedPublishedDate(this.item.publishedAt, timezone)
    );
  }

  get shouldShowTitleLine(): boolean {
    if (!this.item) {
      return false;
    }
    if (
      this.item.platform === SupportedPlatform.FacebookMessenger ||
      this.item.platform === SupportedPlatform.GoogleBusiness
    ) {
      return false;
    }
    const title = (this.item.title || '').trim();
    const content = (this.item.content || '').trim();
    return !!title && title !== content;
  }

  get crossPostPlatformOptions() {
    if (!this.item) {
      return [];
    }
    return getCrossPostPlatformOptions(this.bot, this.item.platform);
  }

  get hasMediaPreview(): boolean {
    if (!this.item) {
      return false;
    }
    return !!(this.item.thumbnailUrl || this.isVideoMedia || this.isCarouselMedia);
  }

  get mediaContainerClass(): string {
    if (!this.item) {
      return 'post-preview-media--landscape';
    }
    if (
      this.item.platform === this.instagramPlatform ||
      this.isCarouselMedia ||
      String(this.item.mediaType || '').toUpperCase() === 'IMAGE'
    ) {
      return 'post-preview-media--square';
    }
    return 'post-preview-media--landscape';
  }

  get isVideoMedia(): boolean {
    if (!this.item) {
      return false;
    }
    const mediaType = String(this.item.mediaType || '').toUpperCase();
    return mediaType === 'VIDEO' || mediaType === 'REELS';
  }

  get isCarouselMedia(): boolean {
    if (!this.item) {
      return false;
    }
    return String(this.item.mediaType || '').toUpperCase() === 'CAROUSEL_ALBUM';
  }

  get captionText(): string {
    if (!this.item) {
      return '';
    }
    return (this.item.content || this.item.title || '').trim() || 'No text content';
  }

  get isCaptionTruncatable(): boolean {
    return this.captionText.length > this.CAPTION_PREVIEW_LENGTH;
  }

  get displayCaption(): string {
    if (!this.isCaptionTruncatable || this.captionExpanded) {
      return this.captionText;
    }
    return `${this.captionText.slice(0, this.CAPTION_PREVIEW_LENGTH).trimEnd()}…`;
  }

  @Watch('show')
  onShowChange(visible: boolean) {
    if (!visible) {
      this.resetState();
      return;
    }
    this.resetSelection();
  }

  @Watch('item')
  onItemChange() {
    this.resetSelection();
    this.thumbnailFailed = false;
    this.captionExpanded = false;
  }

  close() {
    this.$emit('update:show', false);
  }

  resetSelection() {
    this.selectedCrossPostPlatforms = this.crossPostPlatformOptions.map(
      (platform) => platform.value,
    );
  }

  resetState() {
    this.isDeleting = false;
    this.isCrossPosting = false;
    this.thumbnailFailed = false;
    this.captionExpanded = false;
    this.selectedCrossPostPlatforms = [];
  }

  async onDelete() {
    if (!this.item) {
      return;
    }
    const preview = (this.item.content || this.item.title || 'this post').trim().slice(0, 120);
    const confirmed = confirm(
      `Are you sure you want to permanently delete this ${this.platformLabel} post?\n\n"${preview}"`,
    );
    if (!confirmed) {
      return;
    }
    this.isDeleting = true;
    try {
      await deleteSocialFeedPost(this.bot.id, this.item);
      this.$notify.success('Post deleted successfully');
      this.$emit('deleted', this.item);
      this.close();
    } catch (e) {
      const { title, description } = getDeletePostErrorNotification(e, GENERIC_ERROR_DESCRIPTION);
      this.$notify.error({ title, description });
      if (
        this.item.platform === SupportedPlatform.Instagram &&
        /2207085|known meta api issue/i.test(description) &&
        this.item.permalink
      ) {
        const openInInstagram = confirm(
          'Instagram API delete is currently unavailable.\n\nOpen this post in Instagram to delete it manually?',
        );
        if (openInInstagram) {
          window.open(this.item.permalink, '_blank', 'noopener,noreferrer');
        }
      }
    } finally {
      this.isDeleting = false;
    }
  }

  async onCrossPost() {
    if (!this.item || !this.selectedCrossPostPlatforms.length) {
      return;
    }
    this.isCrossPosting = true;
    try {
      const draft = await buildCrossPostDraft(
        this.bot.id,
        this.item,
        this.selectedCrossPostPlatforms,
      );
      setCrossPostDraft(draft);
      this.$emit('cross-posted', {
        item: this.item,
        platforms: this.selectedCrossPostPlatforms.slice(),
      });
      this.close();
      await this.$router.push({
        name: AppRoute.ProjectContentPosts,
        params: { id: this.bot.id },
      });
    } catch (e) {
      this.$notify.error({
        title: 'Could not prepare cross-post',
        description: describePostPublishError(e, GENERIC_ERROR_DESCRIPTION),
      });
    } finally {
      this.isCrossPosting = false;
    }
  }

  formatGooglePostState(state: string): string {
    const labels: Record<string, string> = {
      LIVE: 'Live',
      PROCESSING: 'Processing',
      REJECTED: 'Rejected',
      SCHEDULED: 'Scheduled',
      RECURRING: 'Recurring',
    };
    return labels[state] || state;
  }

  googlePostStateClass(state: string): string {
    const classes: Record<string, string> = {
      LIVE: 'bg-green-100 text-green-800',
      PROCESSING: 'bg-amber-100 text-amber-800',
      REJECTED: 'bg-red-100 text-red-800',
      SCHEDULED: 'bg-blue-100 text-blue-800',
      RECURRING: 'bg-purple-100 text-purple-800',
    };
    return classes[state] || 'bg-gray-100 text-gray-700';
  }
}
